import React, { useEffect, useMemo, useState } from "react";
import { http, ADMIN_TOKEN_KEY } from "../lib/api";
import { toast } from "sonner";
import { Search, FileSpreadsheet, Paperclip, Mail, Check, Archive, Building2 } from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function downloadFile(path, filename) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  try {
    const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("download failed");
    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") || "";
    let name = filename;
    const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
    if (m) {
      try { name = decodeURIComponent(m[1]); } catch { /* ignore */ }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("No se pudo descargar");
  }
}

export default function AdminSPM() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("nuevos");
  const [q, setQ] = useState("");
  const { refresh: refreshNotifs } = useNotifications();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/spm");
      setItems(data || []);
    } catch {
      toast.error("No se pudo cargar el listado");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/admin/spm");
        if (!cancelled) setItems(data || []);
      } catch {
        if (!cancelled) toast.error("No se pudo cargar el listado");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => ({
    todos: items.length,
    nuevos: items.filter((r) => (r.estado || "nuevo") === "nuevo").length,
    atendidos: items.filter((r) => r.estado === "atendido").length,
    cerrados: items.filter((r) => r.estado === "cerrado").length,
  }), [items]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((r) => {
      const estado = r.estado || "nuevo";
      if (tab === "nuevos" && estado !== "nuevo") return false;
      if (tab === "atendidos" && estado !== "atendido") return false;
      if (tab === "cerrados" && estado !== "cerrado") return false;
      if (!ql) return true;
      return [r.direccion_comunidad, r.departamento, r.email_contacto, r.tiene_seguro]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql));
    });
  }, [items, tab, q]);

  const setEstado = async (id, estado) => {
    try {
      await http.patch(`/admin/spm/${id}/estado`, { estado });
      setItems((prev) => prev.map((r) => r.id === id ? { ...r, estado } : r));
      refreshNotifs();
      toast.success(`Marcado como ${estado}`);
    } catch {
      toast.error("No se pudo actualizar el estado");
    }
  };

  return (
    <div className="fade-in-up" data-testid="admin-spm">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: "var(--brand-accent)" }}>
          Registro SPM
        </div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <Building2 size={28} /> Registros de departamentos
        </h1>
        <p className="text-[13.5px] mt-1.5" style={{ color: "var(--text-muted)" }}>
          Registros recibidos desde <code className="px-1.5 py-0.5 rounded text-[11.5px]" style={{ background: "var(--bg-soft)" }}>/SPM</code>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por dirección, depto, email…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border bg-white text-[13px]"
            style={{ borderColor: "var(--border-medium)" }}
            data-testid="spm-search"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: "nuevos", label: "Nuevos" },
          { id: "atendidos", label: "Atendidos" },
          { id: "cerrados", label: "Cerrados" },
          { id: "todos", label: "Todos" },
        ].map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold transition"
              style={{
                background: active ? "var(--brand-navy)" : "white",
                color: active ? "white" : "var(--text-secondary)",
                border: `1px solid ${active ? "var(--brand-navy)" : "var(--border-medium)"}`,
              }}
              data-testid={`spm-tab-${t.id}`}
            >
              {t.label} <span className="opacity-70 ml-1">({counts[t.id]})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-[13px] py-12 text-center" style={{ color: "var(--text-muted)" }}>Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center" style={{ border: "1px solid var(--border-medium)" }}>
          <div className="text-[14px] font-semibold" style={{ color: "var(--text-secondary)" }}>Sin registros</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-medium)" }}>
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Comunidad / Depto</th>
                  <th>¿Seguro?</th>
                  <th>Email</th>
                  <th>Póliza</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const estado = r.estado || "nuevo";
                  return (
                    <tr key={r.id} style={estado === "nuevo" ? { background: "rgba(238,81,71,0.03)" } : undefined}>
                      <td>
                        {estado === "nuevo" && (
                          <span className="text-[10.5px] font-bold px-2 py-1 rounded uppercase tracking-wide" style={{ background: "var(--brand-accent)", color: "white" }}>Nuevo</span>
                        )}
                        {estado === "atendido" && (
                          <span className="text-[10.5px] font-bold px-2 py-1 rounded uppercase tracking-wide" style={{ background: "rgba(34,197,94,0.15)", color: "#15803d" }}>Atendido</span>
                        )}
                        {estado === "cerrado" && (
                          <span className="text-[10.5px] font-bold px-2 py-1 rounded uppercase tracking-wide" style={{ background: "var(--bg-soft)", color: "var(--text-muted)" }}>Cerrado</span>
                        )}
                      </td>
                      <td>
                        <div className="font-semibold text-[13.5px]">{r.direccion_comunidad}</div>
                        <div className="text-[12px] mt-0.5" style={{ color: "var(--text-muted)" }}>Depto: <strong>{r.departamento}</strong></div>
                      </td>
                      <td>
                        {r.tiene_seguro === "si" ? (
                          <span className="text-[11.5px] font-bold px-2 py-1 rounded uppercase tracking-wide" style={{ background: "rgba(34,197,94,0.15)", color: "#15803d" }}>Sí</span>
                        ) : (
                          <span className="text-[11.5px] font-bold px-2 py-1 rounded uppercase tracking-wide" style={{ background: "rgba(238,81,71,0.1)", color: "var(--brand-accent)" }}>No</span>
                        )}
                      </td>
                      <td className="text-[12.5px]">
                        {r.email_contacto ? (
                          <a href={`mailto:${r.email_contacto}`} className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--brand-navy)" }}>
                            <Mail size={11}/> {r.email_contacto}
                          </a>
                        ) : (
                          <span style={{ color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                      <td>
                        {r.tiene_archivo_poliza ? (
                          <button
                            onClick={() => downloadFile(`/admin/spm/${r.id}/poliza`, "poliza.pdf")}
                            className="inline-flex items-center gap-1 text-[12px] font-semibold hover:opacity-80"
                            style={{ color: "var(--brand-accent)" }}
                            data-testid={`spm-pol-${r.id}`}
                          >
                            <Paperclip size={12}/> Descargar
                          </button>
                        ) : (
                          <span className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                      <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                        {(r.created_at || "").slice(0, 10)}
                      </td>
                      <td>
                        <div className="flex flex-col gap-1">
                          {estado !== "atendido" && (
                            <button onClick={() => setEstado(r.id, "atendido")} className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded hover:opacity-80" style={{ background: "rgba(34,197,94,0.1)", color: "#15803d" }} data-testid={`spm-mark-atendido-${r.id}`}>
                              <Check size={11}/> Atendido
                            </button>
                          )}
                          {estado !== "cerrado" && (
                            <button onClick={() => setEstado(r.id, "cerrado")} className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded hover:opacity-80" style={{ background: "var(--bg-soft)", color: "var(--text-muted)" }} data-testid={`spm-mark-cerrado-${r.id}`}>
                              <Archive size={11}/> Cerrar
                            </button>
                          )}
                          {estado !== "nuevo" && (
                            <button onClick={() => setEstado(r.id, "nuevo")} className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded hover:opacity-80" style={{ background: "rgba(238,81,71,0.08)", color: "var(--brand-accent)" }} data-testid={`spm-mark-nuevo-${r.id}`}>
                              Reabrir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
